export interface IBadgeDefinition {
    key: string;
    label: string;
    description: string;
    icon: string;
    image: string;
    category: string;
    views: string[];
    tier?: number;
    enabled: boolean;
}

export const BADGE_DEFINITIONS: { [key: string]: IBadgeDefinition } = {
    quickResponder: {
        key: 'quickResponder',
        label: 'Quick Responder',
        description: 'Worker accepts a shift offer within 5 minutes of receiving it.',
        icon: 'badge-quick-responder',
        image: 'quick_responder.png',
        category: 'responsiveness',
        views: ['profile', 'shift', 'client_review', 'admin'],
        enabled: true,
    },
    alwaysConfirmed: {
        key: 'alwaysConfirmed',
        label: 'Always Confirmed',
        description: 'Worker has never cancelled a confirmed shift.',
        icon: 'badge-always-confirmed',
        image: 'always_confirmed.png',
        category: 'reliability',
        views: ['profile', 'client_review', 'admin'],
        enabled: true,
    },
    highAcceptanceRate: {
        key: 'highAcceptanceRate',
        label: 'High Acceptance Rate',
        description: 'Worker accepts ≥ 30% of shift offers sent to them.',
        icon: 'badge-high-acceptance',
        image: 'high_acceptance_rate.png',
        category: 'responsiveness',
        views: ['profile', 'client_review', 'admin'],
        enabled: true,
    },
    activeThisWeek: {
        key: 'activeThisWeek',
        label: 'Active This Week',
        description: 'Worker has completed at least 1 shift in the current calendar week.',
        icon: 'badge-active-this-week',
        image: 'active_this_week.png',
        category: 'activity',
        views: ['profile', 'shift', 'admin'],
        enabled: true,
    },
    zeroNoShows: {
        key: 'zeroNoShows',
        label: 'Zero No-Shows',
        description: 'Worker has never missed a shift without cancelling.',
        icon: 'badge-zero-no-shows',
        image: 'zero_no_shows.png',
        category: 'reliability',
        views: ['profile', 'client_review', 'admin'],
        enabled: true,
    },
    rightOnTime: {
        key: 'rightOnTime',
        label: 'Right on Time',
        description: 'Worker has clocked in on time for 98% of all shifts.',
        icon: 'badge-right-on-time',
        image: 'right_on_time.png',
        category: 'reliability',
        views: ['profile', 'shift', 'client_review', 'admin'],
        enabled: true,
    },
    completionStreak: {
        key: 'completionStreak',
        label: 'Completion Streak',
        description: 'Worker has completed 10 shifts in a row without a cancellation or no-show.',
        icon: 'badge-completion-streak',
        image: 'completion_streak.png',
        category: 'reliability',
        views: ['profile', 'admin'],
        enabled: true,
    },
    lastMinuteHero: {
        key: 'lastMinuteHero',
        label: 'Last Minute Hero',
        description: 'Worker has accepted and completed 5 shifts posted within 24 hours of start time.',
        icon: 'badge-last-minute-hero',
        image: 'last_minute_hero.png',
        category: 'responsiveness',
        views: ['profile', 'shift', 'admin'],
        enabled: true,
    },
    fiftyShiftsCompleted: {
        key: 'fiftyShiftsCompleted',
        label: '50 Shifts Completed',
        description: 'Worker has completed a cumulative total of 50 shifts.',
        icon: 'badge-50-shifts',
        image: '50_shifts_completed.png',
        category: 'milestone',
        views: ['profile', 'client_review', 'admin'],
        enabled: true,
    },
    hundredShiftsCompleted: {
        key: 'hundredShiftsCompleted',
        label: '100 Shifts Completed',
        description: 'Worker has completed a cumulative total of 100 shifts.',
        icon: 'badge-100-shifts',
        image: '100_shifts_completed.png',
        category: 'milestone',
        views: ['profile', 'client_review', 'admin'],
        enabled: true,
    },
    platformSpecialistV1: {
        key: 'platformSpecialistV1',
        label: 'Platform Specialist',
        description: 'Worker has worked at 10 different client facilities.',
        icon: 'badge-platform-specialist',
        image: 'platform_specialist.png',
        category: 'milestone',
        tier: 1,
        views: ['profile', 'client_review', 'admin'],
        enabled: true,
    },
    oneYearActiveV1: {
        key: 'oneYearActiveV1',
        label: '1 Year Active',
        description: "Worker's profile has been active and in good standing for 365 days.",
        icon: 'badge-1-year-active',
        image: '1_year_active.png',
        category: 'milestone',
        tier: 1,
        views: ['profile', 'admin'],
        enabled: true,
    },
    fiveStarAverage: {
        key: 'fiveStarAverage',
        label: '5-Star Average',
        description: "Worker's average client rating across all rated shifts is 5.0.",
        icon: 'badge-5-star',
        image: '5_star_average.png',
        category: 'rating',
        views: ['profile', 'client_review', 'admin'],
        enabled: true,
    },
    topRatedPro: {
        key: 'topRatedPro',
        label: 'Top Rated Pro',
        description: 'Worker maintains a reliability rating above 95% across a minimum of 20 shifts.',
        icon: 'badge-top-rated',
        image: 'top_rated_pro.png',
        category: 'rating',
        views: ['profile', 'shift', 'client_review', 'admin'],
        enabled: true,
    },
    platformSpecialistV2: {
        key: 'platformSpecialistV2',
        label: 'Platform Specialist',
        description: 'Worker has completed 20 shifts in the last 3 months.',
        icon: 'badge-platform-specialist-v2',
        image: 'platform_specialist.png',
        category: 'milestone',
        tier: 2,
        views: ['profile', 'client_review', 'admin'],
        enabled: true,
    },
    oneYearActiveV2: {
        key: 'oneYearActiveV2',
        label: '1 Year Active',
        description: 'Worker has been active for 1 year and completed at least 1 shift per month in the last 6 months.',
        icon: 'badge-1-year-active-v2',
        image: '1_year_active.png',
        category: 'milestone',
        tier: 2,
        views: ['profile', 'admin'],
        enabled: true,
    },
    profileComplete: {
        key: 'profileComplete',
        label: 'Profile Complete',
        description: 'Worker has filled out all required profile fields and uploaded documents.',
        icon: 'badge-profile-complete',
        image: 'profile_complete.png',
        category: 'verification',
        views: ['profile', 'admin'],
        enabled: true,
    },
    biometricVerifiedV1: {
        key: 'biometricVerifiedV1',
        label: 'Biometric Verified',
        description: 'Worker has completed biometric identity verification.',
        icon: 'badge-biometric-verified',
        image: 'biometric_verified.png',
        category: 'verification',
        tier: 1,
        views: ['profile', 'client_review', 'admin'],
        enabled: true,
    },
    biometricVerifiedV2: {
        key: 'biometricVerifiedV2',
        label: 'Biometric Verified',
        description: 'Worker has completed an additional or re-verified biometric check on a shift.',
        icon: 'badge-biometric-verified-v2',
        image: 'biometric_verified.png',
        category: 'verification',
        tier: 2,
        views: ['profile', 'client_review', 'admin'],
        enabled: false,
    },
    credentialsVerified: {
        key: 'credentialsVerified',
        label: 'Credentials Verified',
        description: 'Worker has had all professional credentials verified by the platform.',
        icon: 'badge-credentials-verified',
        image: 'credentials_verified.png',
        category: 'verification',
        views: ['profile', 'client_review', 'admin'],
        enabled: true,
    },
};

export function getBadgeDefinition(badgeKey: string): IBadgeDefinition | null {
    return BADGE_DEFINITIONS[badgeKey] || null;
}

